import React from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  withStyles,
} from "@material-ui/core";
import { format, parseISO } from "date-fns";
import CustomForm from "../CustomForm";

// Pré-visualização: o formulário renderizado pelo MESMO componente que o
// preenchimento usa (CustomForm), sobre uma cópia do layout em edição.
//
// É interativo de propósito — dá para digitar, marcar e lançar linhas na
// tabela — porque a pergunta que o builder precisa responder é "esse formulário
// funciona?", e não só "como ele fica?". Nada aqui é salvo: o estado morre com
// o diálogo, e os handlers são versões enxutas dos do AddDialogForm.
class PreviewDialog extends React.Component {
  constructor(props) {
    super(props);

    const fields = JSON.parse(JSON.stringify(props.fields || []));
    this.state = { fields };

    // O canvas de assinatura precisa de um ref por assinatura, na mesma ordem
    // em que o preenchimento os numera.
    this.signatureRef = fields
      .filter((field) => field.type === "signature")
      .map(() => React.createRef());
    this.signatureRefTable = fields.reduce((acc, field) => {
      (field.columns || []).forEach((column) => {
        if (column.type === "signature") acc.push(React.createRef());
      });
      return acc;
    }, []);
  }

  // Atualiza um campo por índice (imutável, como no resto do builder).
  patchField = (index, patch) => {
    this.setState({
      fields: this.state.fields.map((field, i) =>
        i === index ? { ...field, ...patch } : field
      ),
    });
  };

  patchColumn = (index, columnIndex, patch) => {
    const columns = (this.state.fields[index].columns || []).map((column, i) =>
      i === columnIndex ? { ...column, ...patch } : column
    );
    this.patchField(index, { columns });
  };

  patchOption = (index, optionIndex, patch, exclusive) => {
    const options = (this.state.fields[index].options || []).map((option, i) => {
      if (i === optionIndex) return { ...option, ...patch };
      // Sem multi, marcar uma desmarca as outras (comportamento de radio).
      return exclusive && option.type === "checkbox" ? { ...option, checked: false } : option;
    });
    this.patchField(index, { options });
  };

  patchColumnOption = (index, columnIndex, optionIndex, patch, exclusive) => {
    const column = (this.state.fields[index].columns || [])[columnIndex] || {};
    const options = (column.options || []).map((option, i) => {
      if (i === optionIndex) return { ...option, ...patch };
      return exclusive && option.type === "checkbox" ? { ...option, checked: false } : option;
    });
    this.patchColumn(index, columnIndex, { options });
  };

  // "Achata" as colunas numa linha, igual ao preenchimento (checkbox vira texto
  // com as marcadas + observação; data vira dd/MM/yyyy; assinatura, o arquivo).
  flattenRow = (columns) =>
    columns.map((column) => {
      if (column.type === "checkbox") {
        const checked = (column.options || [])
          .filter((option) => option.checked === true)
          .map((option) => option.label)
          .join(", ");
        const obs = (column.options || [])
          .filter((option) => option.type === "obs" && option.value)
          .map((option) => option.value)
          .join();
        return obs ? `${checked} ${obs}` : checked;
      }
      if (column.type === "signature") return column.source;
      if (column.type === "date" && column.value) {
        try {
          return format(parseISO(column.value), "dd/MM/yyyy");
        } catch (e) {
          return column.value;
        }
      }
      return column.value;
    });

  clearColumns = (columns) =>
    columns.map((column) => {
      if (column.type === "checkbox") {
        return {
          ...column,
          options: (column.options || []).map((option) => ({
            label: option.label,
            type: option.type,
            checked: false,
          })),
        };
      }
      if (column.type === "signature") return { ...column, source: undefined };
      return { ...column, value: null };
    });

  addTableRow = (index) => {
    const field = this.state.fields[index];
    const columns = field.columns || [];
    this.patchField(index, {
      body: [...(field.body || []), this.flattenRow(columns)],
      columns: this.clearColumns(columns),
    });
  };

  deleteTableRow = (index, rowIndex) => {
    const field = this.state.fields[index];
    this.patchField(index, {
      body: (field.body || []).filter((_, i) => i !== rowIndex),
    });
  };

  render() {
    const { classes } = this.props;

    return (
      <Dialog open fullWidth maxWidth="lg" onClose={this.props.onClose}>
        <DialogTitle className={classes.title}>
          Pré-visualização — {this.props.formName || "Formulário"}
        </DialogTitle>
        <DialogContent>
          <Typography variant="caption" color="textSecondary" className={classes.hint}>
            Dá para preencher à vontade para testar o formulário: nada aqui é
            salvo, e assinatura/upload ficam desligados.
          </Typography>
          <CustomForm
            add
            fields={this.state.fields}
            users={this.props.users || []}
            editingRowIndex={false}
            id_data_form={null}
            signatureRef={this.signatureRef}
            signatureRefTable={this.signatureRefTable}
            handleChangeTextForm={(event, index) =>
              this.patchField(index, { value: event.target.value })
            }
            handleSelectOptions={(event, index) =>
              this.patchField(index, { value: event.target.value })
            }
            handleSelectApiOptions={(event, index) =>
              this.patchField(index, { value: event.target.value })
            }
            handleSelectFunc={(index, value) => this.patchField(index, { value })}
            handleChangeCheckboxForm={(event, index, optionIndex) =>
              this.patchOption(index, optionIndex, { checked: event.target.checked }, true)
            }
            handleChangeCheckboxFormMulti={(event, index, optionIndex) =>
              this.patchOption(index, optionIndex, { checked: event.target.checked }, false)
            }
            handleChangeCheckboxObs={(event, index, optionIndex) =>
              this.patchOption(index, optionIndex, { value: event.target.value }, false)
            }
            handleChangeTextTableForm={(event, index, columnIndex) =>
              this.patchColumn(index, columnIndex, { value: event.target.value })
            }
            handleChangeSelectFormTable={(event, index, columnIndex) =>
              this.patchColumn(index, columnIndex, { value: event.target.value })
            }
            handleSelectFuncFormTable={(value, index, columnIndex) =>
              this.patchColumn(index, columnIndex, { value })
            }
            handleChangeCheckboxFormTable={(event, index, columnIndex, optionIndex) =>
              this.patchColumnOption(
                index,
                columnIndex,
                optionIndex,
                { checked: event.target.checked },
                true
              )
            }
            handleChangeCheckboxFormTableMulti={(event, index, columnIndex, optionIndex) =>
              this.patchColumnOption(
                index,
                columnIndex,
                optionIndex,
                { checked: event.target.checked },
                false
              )
            }
            handleChangeCheckboxObsTable={(event, index, columnIndex, optionIndex) =>
              this.patchColumnOption(
                index,
                columnIndex,
                optionIndex,
                { value: event.target.value },
                false
              )
            }
            handleAddDatasTableForm={this.addTableRow}
            handleEditDatasTableForm={this.addTableRow}
            deleteBodyTable={this.deleteTableRow}
            editBodyTable={() => {}}
            handleProductOrderChange={(index, patch) => this.patchField(index, patch)}
            handleFormLinkChange={(index, patch) => this.patchField(index, patch)}
            handleSignature={() => {}}
            handleClearSignature={(signatureIndex) => {
              const ref = this.signatureRef[signatureIndex];
              if (ref && ref.current) ref.current.clear();
            }}
            handleSignatureTable={() => {}}
            handleClearSignatureTable={(signatureIndex) => {
              const ref = this.signatureRefTable[signatureIndex];
              if (ref && ref.current) ref.current.clear();
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button variant="contained" color="primary" onClick={this.props.onClose}>
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
}

const styles = (theme) => ({
  title: {
    backgroundColor: "#023575",
    color: "#fff",
  },
  hint: {
    display: "block",
    marginBottom: theme.spacing(1),
  },
});

export default withStyles(styles)(PreviewDialog);
